import { FormControl, FormHelperText, makeStyles } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import existing type definitions – these types reflect the shape used by TreeWithExclusionFilterInput and TagsWithExclusionFilterInput.
import { Classifier, IndustryCodeFilter } from '../../../generated/model/models';
import { WildcardTagsWithExclusionTagsFilter } from '../../../generated/model/wildcardTagsWithExclusionTagsFilter';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';
import TreeInput from '../../Inputs/TreeComponent/TreeInput';
import TagsFilterInput, { TagsFilterLabelFormatters } from '../TagsFilterInput/TagsFilterInput';

export interface CompanyActivityFilter {
  nace: IndustryCodeFilter;
  sic: IndustryCodeFilter;
  keywords: WildcardTagsWithExclusionTagsFilter;
  naceMandatory: boolean;
  sicMandatory: boolean;
  keywordsMandatory: boolean;
}

interface Props {
  active: boolean;
  onChange: (value: CompanyActivityFilter) => void;
  defaultValue?: CompanyActivityFilter;
  itemsNace?: any[];
  itemsSic?: any[];
  labelFormatter?: TagsFilterLabelFormatters;
}

export const FALLBACK_DEFAULT_COMPANY_ACTIVITY_FILTER_STATE = {
  nace: { value: { tags: { includeValues: [], excludeValues: [] } }, isPrimaryOnly: false },
  sic: { value: { tags: { includeValues: [], excludeValues: [] } }, isPrimaryOnly: false },
  // Using the structure from WildcardTagsWithExclusionTagsFilter
  keywords: { value: { tags: { includeValues: [], excludeValues: [] } }, exactSearch: false },
  // Mandatory flags
  naceMandatory: false,
  sicMandatory: false,
  keywordsMandatory: false,
};

function modifyClassifiers(level: number, items: Classifier[]): any[] {
  return items.map((item) => {
    if (!item.id) {
      throw new Error('Item id is undefined');
    }
    if (item.values) {
      return {
        ...item,
        id: item.id + '*' + level,
        values: modifyClassifiers(level + 1, [
          { id: item.id, name: item.name ? item.name + ' (Exact activity)' : '' },
          ...item.values,
        ]),
      };
    }
    return { id: item.id, name: item.name || '' };
  });
}

const CompanyActivityFilterInput: React.FC<Props> = React.memo(
  ({
    active,
    labelFormatter,
    onChange,
    defaultValue = FALLBACK_DEFAULT_COMPANY_ACTIVITY_FILTER_STATE,
    itemsNace = [],
    itemsSic = [],
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [state, setState] = useState<CompanyActivityFilter>(defaultValue);
    const [toggleRulesOpenState, setToggleRulesOpenState] = useState<boolean>(false);
    const [toggleOpenState, setToggleOpenState] = useState<boolean>(false);

    const transformedNace = useMemo(() => {
      return modifyClassifiers(0, itemsNace);
    }, [itemsNace]);

    const transformedSic = useMemo(() => {
      return modifyClassifiers(0, itemsSic);
    }, [itemsSic]);

    // Synchronize with default values and propagate changes as in the original components.
    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state);

    // Auto-open exclusions if any excluded values are present.
    useEffect(() => {
      if (
        state.nace.value.tags.excludeValues.length > 0 ||
        state.sic.value.tags.excludeValues.length > 0 ||
        state.keywords.value.tags.excludeValues.length > 0
      ) {
        setToggleOpenState(true);
      }
    }, [
      state.nace.value.tags.excludeValues,
      state.sic.value.tags.excludeValues,
      state.keywords.value.tags.excludeValues,
    ]);

    function createReadableStatement(): any[] {
      const rs: any[] = [];

      function statementLine(
        f: IndustryCodeFilter | WildcardTagsWithExclusionTagsFilter,
        mandatory: boolean,
      ): string {
        const includeValues = f.value.tags.includeValues ?? [];
        const excludeValues = f.value.tags.excludeValues ?? [];
        const includeText = includeValues.length ? includeValues.join(', ') : '';
        const excludeText = excludeValues.length ? ' -(' + excludeValues.join(', ') + ')' : '';

        return (mandatory ? '+(' + includeText + ')' : includeText) + excludeText;
      }

      function keywordsRequiredText(mandatory: boolean): string {
        return mandatory ? 'Keywords must match' : 'Keywords are optional';
      }

      function naceRequiredText(mandatory: boolean): string {
        return mandatory ? 'NACE must match' : 'NACE match is optional';
      }

      function sicRequiredText(mandatory: boolean): string {
        return mandatory ? 'SIC must match' : 'SIC match is optional';
      }

      // function keywordsStatement(
      //   f: WildcardTagsWithExclusionTagsFilter,
      //   mandatory: boolean,
      // ): string {
      //   const includeText = f.value.tags.includeValues.length
      //     ? state.nace.value.tags.includeValues.join(', ')
      //     : '';
      //   const excludeText = f.value.tags.excludeValues.length
      //     ? ' -(' + state.nace.value.tags.excludeValues.join(', ') + ')'
      //     : '';

      //   return (mandatory ? '+(' + includeText + ')' : '') + excludeText;
      // }

      const keywordsRequiredStatement = keywordsRequiredText(state.keywordsMandatory);
      if (keywordsRequiredStatement) {
        rs.push({ label: 'Keywords', value: keywordsRequiredStatement });
      }

      const keywordsStatement = statementLine(state.keywords, state.keywordsMandatory);
      if (keywordsStatement) {
        rs.push({ label: 'Keywords', value: keywordsStatement });
      }

      const naceRequiredStatement = naceRequiredText(state.naceMandatory);
      if (naceRequiredStatement) {
        rs.push({ label: 'NACE', value: naceRequiredStatement });
      }

      const naceStatement = statementLine(state.nace, state.naceMandatory);

      if (naceStatement) {
        rs.push({ label: 'NACE', value: naceStatement });
      }

      const sicRequiredStatement = sicRequiredText(state.sicMandatory);
      if (sicRequiredStatement) {
        rs.push({ label: 'SIC', value: sicRequiredStatement });
      }

      const sicStatement = statementLine(state.sic, state.sicMandatory);
      if (sicStatement) {
        rs.push({ label: 'SIC', value: sicStatement });
      }

      return rs;
    }

    // Build summary rows for non-active view.
    const rs: any[] = createReadableStatement();

    return (
      <>
        {active ? (
          <FormControl className={`form-control ${classes.root}`}>
            {/* Included Filters Section */}
            <div className={classes.section}>
              <div className={classes.filterGroup}>
                <TagsFilterInput
                  field="includeKeywordValues"
                  active={active}
                  defaultValue={{ value: state.keywords.value.tags.includeValues }}
                  labelFormatter={labelFormatter}
                  onChange={(tags) =>
                    setState((prev) => ({
                      ...prev,
                      keywords: {
                        ...prev.keywords,
                        value: {
                          ...prev.keywords.value,
                          tags: {
                            includeValues: tags.value,
                            excludeValues: prev.keywords.value.tags.excludeValues,
                          },
                        },
                      },
                    }))
                  }
                />
              </div>

              {/* NACE selection */}
              <div className={classes.filterGroup}>
                <TreeInput
                  items={transformedNace}
                  onChange={(values) =>
                    setState((prev) => ({
                      ...prev,
                      nace: {
                        ...prev.nace,
                        value: {
                          ...prev.nace.value,
                          tags: {
                            includeValues: values,
                            excludeValues: prev.nace.value.tags.excludeValues,
                          },
                        },
                      },
                    }))
                  }
                  defaultValue={state.nace.value.tags.includeValues}
                  chunkLoading={true}
                  placeholder={t('app:selectNaceCodes')}
                />
              </div>
              {/* SIC selection */}
              <div className={classes.filterGroup}>
                <TreeInput
                  items={transformedSic}
                  onChange={(values) =>
                    setState((prev) => ({
                      ...prev,
                      sic: {
                        ...prev.sic,
                        value: {
                          ...prev.sic.value,
                          tags: {
                            includeValues: values,
                            excludeValues: prev.sic.value.tags.excludeValues,
                          },
                        },
                      },
                    }))
                  }
                  defaultValue={state.sic.value.tags.includeValues}
                  chunkLoading={true}
                  placeholder={t('app:selectSICCodes')}
                />
              </div>
            </div>

            {/* Toggle button for Match Rules */}
            <div
              className={classes.toggler}
              onClick={() => setToggleRulesOpenState(!toggleRulesOpenState)}
            >
              <FormHelperText>{t('app:matchRules')}</FormHelperText>
              {toggleRulesOpenState ? <ArrowDropUp /> : <ArrowDropDown />}
            </div>

            {/* Match Rules Section */}
            {toggleRulesOpenState && (
              <div className={classes.section}>
                {/* Keywords with Mandatory Switch */}
                <div className={classes.filterGroup}>
                  <SwitchCompaniesUnknowns
                    checked={state.keywordsMandatory}
                    onChange={(value) =>
                      setState((prev) => ({ ...prev, keywordsMandatory: value }))
                    }
                    unknownSwitchLabel={t(
                      'companies:filterInputs.companyActivityFilterKeywordsMandatory',
                    )}
                  />
                  <SwitchCompaniesUnknowns
                    checked={state.naceMandatory}
                    onChange={(value) => setState((prev) => ({ ...prev, naceMandatory: value }))}
                    unknownSwitchLabel={t(
                      'companies:filterInputs.companyActivityFilterNaceMandatory',
                    )}
                  />
                  <SwitchCompaniesUnknowns
                    checked={state.sicMandatory}
                    onChange={(value) => setState((prev) => ({ ...prev, sicMandatory: value }))}
                    unknownSwitchLabel={t(
                      'companies:filterInputs.companyActivityFilterSicMandatory',
                    )}
                  />
                </div>
              </div>
            )}

            {/* Toggle button for Excluded Filters */}
            <div className={classes.toggler} onClick={() => setToggleOpenState(!toggleOpenState)}>
              <FormHelperText>{t('app:notIncluded')}</FormHelperText>
              {toggleOpenState ? <ArrowDropUp /> : <ArrowDropDown />}
            </div>

            {/* Excluded Filters Section */}
            {toggleOpenState && (
              <div className={classes.section}>
                <div className={classes.filterGroup}>
                  <TagsFilterInput
                    field="excludeKeywordValues"
                    active={active}
                    defaultValue={{ value: state.keywords.value.tags.excludeValues }}
                    labelFormatter={labelFormatter}
                    onChange={(tags) =>
                      setState((prev) => ({
                        ...prev,
                        keywords: {
                          ...prev.keywords,
                          value: {
                            ...prev.keywords.value,
                            tags: {
                              includeValues: prev.keywords.value.tags.includeValues,
                              excludeValues: tags.value,
                            },
                          },
                        },
                      }))
                    }
                  />
                </div>
                <div className={classes.filterGroup}>
                  <TreeInput
                    items={transformedNace}
                    onChange={(values) =>
                      setState((prev) => ({
                        ...prev,
                        nace: {
                          ...prev.nace,
                          value: {
                            ...prev.nace.value,
                            tags: {
                              includeValues: prev.nace.value.tags.includeValues,
                              excludeValues: values,
                            },
                          },
                        },
                      }))
                    }
                    emptyLabel={'companies:filterInputs.companyActivityFilterNace'}
                    defaultValue={state.nace.value.tags.excludeValues}
                    chunkLoading={true}
                  />
                </div>
                <div className={classes.filterGroup}>
                  <TreeInput
                    items={transformedSic}
                    onChange={(values) =>
                      setState((prev) => ({
                        ...prev,
                        sic: {
                          ...prev.sic,
                          value: {
                            ...prev.sic.value,
                            tags: {
                              includeValues: prev.sic.value.tags.includeValues,
                              excludeValues: values,
                            },
                          },
                        },
                      }))
                    }
                    emptyLabel={'companies:filterInputs.companyActivityFilterSic'}
                    defaultValue={state.sic.value.tags.excludeValues}
                    chunkLoading={true}
                  />
                </div>
              </div>
            )}
          </FormControl>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme) => ({
  root: {
    '.Mui-expanded': {
      margin: 0,
    },
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  filterGroup: {
    marginBottom: theme.spacing(2),
  },
  toggler: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: theme.typography.fontSize - 2,
    cursor: 'pointer',
  },
}));

export default CompanyActivityFilterInput;
